import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { siteConfigService, SiteConfig } from '../services/siteConfig';
import { logoAPI } from '../services/logoAPI';

interface SiteConfigContextType {
  config: SiteConfig | null;
  loading: boolean;
  refreshConfig: () => Promise<void>;
  getSiteName: () => string;
  getCompanyName: () => string;
  getWelcomeMessage: () => string;
  getFooterText: () => string;
  getFooterCopyright: () => string;
  getFooterTagline: () => string;
}

const SiteConfigContext = createContext<SiteConfigContextType | undefined>(undefined);

export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  if (context === undefined) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return context;
};

// Helper to set or create a meta tag
const setMetaTag = (name: string, content: string, property?: boolean) => {
  if (!content) return;
  const attr = property ? 'property' : 'name';
  let tag = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.content = content;
};

const setLinkTag = (rel: string, href: string) => {
  if (!href) return;
  let tag = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (!tag) {
    tag = document.createElement('link');
    tag.rel = rel;
    document.head.appendChild(tag);
  }
  tag.href = href;
};

const updateMetaTags = (config: SiteConfig) => {
  // Page title
  if (config.meta_title) {
    document.title = config.meta_title;
  } else if (config.site_name) {
    document.title = config.site_name;
  }

  // Basic meta tags
  setMetaTag('description', config.meta_description || config.site_description);
  if (config.meta_keywords) {
    setMetaTag('keywords', config.meta_keywords);
  }

  // Open Graph tags
  setMetaTag('og:title', config.og_title || config.meta_title || config.site_name, true);
  setMetaTag('og:description', config.og_description || config.meta_description || config.site_description, true);
  setMetaTag('og:type', 'website', true);
  if (config.og_image_url) {
    setMetaTag('og:image', config.og_image_url, true);
  }
  if (config.canonical_url) {
    setMetaTag('og:url', config.canonical_url, true);
    setLinkTag('canonical', config.canonical_url);
  }

  // Twitter Card
  setMetaTag('twitter:card', 'summary_large_image');
  setMetaTag('twitter:title', config.og_title || config.meta_title || config.site_name);
  setMetaTag('twitter:description', config.og_description || config.meta_description || config.site_description);
  if (config.og_image_url) {
    setMetaTag('twitter:image', config.og_image_url);
  }
};

interface SiteConfigProviderProps {
  children: ReactNode;
}

export const SiteConfigProvider: React.FC<SiteConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const loadConfig = async (forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      // Try to get authenticated config first, fallback to public
      const token = localStorage.getItem('token');
      const newConfig = await siteConfigService.getConfig(!!token, forceRefresh);
      setConfig(newConfig);
    } catch (error) {
      console.error('Failed to load site config:', error);
      // Set default config on error
      setConfig(await siteConfigService.getConfig(false, forceRefresh));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Force refresh on initial load to get latest config
    loadConfig(true);
  }, []);

  // Update meta tags whenever config changes
  useEffect(() => {
    if (config) {
      updateMetaTags(config);
    }
  }, [config]);

  // Dynamically inject favicon from API
  useEffect(() => {
    const loadFavicon = async () => {
      try {
        const response = await logoAPI.getActiveLogos();
        const faviconData = response.data?.favicon;
        if (faviconData?.image_url) {
          setLinkTag('icon', faviconData.image_url);
          // Also set apple-touch-icon if available
          setLinkTag('apple-touch-icon', faviconData.image_url);
        }
      } catch {
        // Keep default favicon if API fails
      }
    };
    loadFavicon();
  }, []);

  const refreshConfig = async () => {
    siteConfigService.clearCache();
    await loadConfig(true);
  };

  const getSiteName = () => {
    return config?.site_name || 'Wholesale Bakery';
  };

  const getCompanyName = () => {
    return config?.company_name || 'Wholesale Bakery';
  };

  const getWelcomeMessage = () => {
    return config?.welcome_message || 'Welcome to our wholesale bakery platform!';
  };

  const getFooterText = () => {
    return config?.footer_text || 'Wholesale Management System';
  };

  const getFooterCopyright = () => {
    if (config?.footer_copyright) return config.footer_copyright;
    const name = config?.company_name || config?.site_name || 'Wholesale Bakery';
    return `© ${new Date().getFullYear()} ${name}. All rights reserved.`;
  };

  const getFooterTagline = () => {
    return config?.footer_tagline || 'Wholesale bakery management platform.\nBuilt with care in Texas.';
  };

  const value = {
    config,
    loading,
    refreshConfig,
    getSiteName,
    getCompanyName,
    getWelcomeMessage,
    getFooterText,
    getFooterCopyright,
    getFooterTagline
  };

  return (
    <SiteConfigContext.Provider value={value}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export default SiteConfigProvider;